import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../../../config/api";
import { getUser } from "../../../config/auth";
import { canCreateTask } from "../../../config/rbac";
import Pagination, { usePagination } from "../../../components/Pagination";
import Loader from "../../../components/Loader";
import EmptyState from "../../../components/EmptyState";
import PageHeader from "../../../components/PageHeader";

const PAGE_SIZE = 10;

export default function ProjectsPage() {
  const navigate = useNavigate();
  const user = getUser();
  const role = user?.role || "Staff";
  const canCreate = canCreateTask(role);

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");

  const filteredProjects = useMemo(() => {
    if (!search.trim()) return projects;
    const q = search.toLowerCase();
    return projects.filter((p) => p.name?.toLowerCase().includes(q) || p.description?.toLowerCase().includes(q));
  }, [projects, search]);

  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [projectUrl, setProjectUrl] = useState("");
  const [modalError, setModalError] = useState("");
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiFetch("/api/projects");
      const data = res.ok ? await res.json() : [];
      setProjects(Array.isArray(data) ? data : []);
    } catch {
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const createProject = async () => {
    setModalError("");
    if (!name.trim()) return setModalError("Project name is required.");
    if (projectUrl.trim() && !/^https?:\/\//i.test(projectUrl.trim()))
      return setModalError("Reference URL must start with http:// or https://");
    setCreating(true);
    try {
      const res = await apiFetch("/api/projects", {
        method: "POST",
        body: JSON.stringify({
          name: name.trim(),
          description: description || null,
          projectUrl: projectUrl.trim() || null,
        }),
      });
      if (!res.ok) {
        const msg = await res.text();
        return setModalError(msg || "Failed to create project.");
      }
      setName("");
      setDescription("");
      setProjectUrl("");
      setModalError("");
      setShowCreate(false);
      load();
    } catch (e) {
      setModalError(e?.message || "Failed to create project.");
    } finally {
      setCreating(false);
    }
  };
  const { totalPages, safePage, paginated } = usePagination(
    filteredProjects,
    PAGE_SIZE,
  );
  const currentPage = safePage(page);
  const pagedProjects = paginated(currentPage);

  return (
    <div style={{ maxWidth: 1100, display: "grid", gap: 28 }}>
      {/* ================= HEADER ================= */}
      <PageHeader
        title="Projects"
        subtitle="Manage strategic initiatives and operational workstreams."
        actions={
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <div style={{ position: "relative" }}>
              <span style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", fontSize: 12, color: "var(--text-3)", pointerEvents: "none" }}>🔍</span>
              <input
                value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                placeholder="Search projects..."
                style={{
                  padding: "8px 12px 8px 28px", borderRadius: 20, fontSize: 13,
                  border: search ? "1.5px solid var(--brand)" : "1px solid var(--border-strong)",
                  background: search ? "var(--brand-soft)" : "var(--surface-2)",
                  color: "var(--text-1)", outline: "none", width: 200,
                }}
              />
            </div>
            {search && (
              <button
                onClick={() => { setSearch(""); setPage(1); }}
                style={{ padding: "8px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600, border: "1px solid #fecdd3", background: "var(--danger-bg)", color: "var(--danger-text)", cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}
              >
                <span style={{ fontSize: 10 }}>✕</span> Clear
              </button>
            )}
            {canCreate && (
              <button className="btn" style={{ padding: "10px 18px", fontWeight: 600 }} onClick={() => setShowCreate(true)}>
                + Create Project
              </button>
            )}
          </div>
        }
      />

      {/* ================= PROJECT LIST ================= */}
      <div
        style={{
          display: "grid",
          gap: 18,
        }}
      >
        {loading ? (
          [0, 1, 2].map((i) => (
            <div key={i} className="glass-card" style={{ padding: 22 }}>
              <Loader type="skeleton" height={18} width="45%" style={{ marginBottom: 12 }} />
              <Loader type="skeleton" height={13} width="75%" />
            </div>
          ))
        ) : filteredProjects.length === 0 ? (
          <div className="glass-card">
            <EmptyState
              icon="🗂️"
              title={search ? "No projects match your search" : "No projects created yet"}
              subtitle={search ? "Try a different keyword." : canCreate ? 'Click "+ Create Project" to add your first project.' : "Projects will appear here once created by your admin."}
            />
          </div>
        ) : (
          <>
            {pagedProjects.map((p) => (
              <div
                key={p.id}
                className="glass-card"
                style={{
                  padding: 22,
                  transition: "transform 0.15s ease, box-shadow 0.15s ease",
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-3px)";
                  e.currentTarget.style.boxShadow = "0 14px 35px rgba(0,0,0,0.10)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "";
                }}
                onClick={() => navigate(`/projects/${p.id}`)}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ fontWeight: 700, fontSize: 16 }}>{p.name}</div>
                  {p.projectUrl && /^https?:\/\//i.test(p.projectUrl) && (
                    <a
                      href={p.projectUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      style={{ fontSize: 13, color: "#2563eb", textDecoration: "none", whiteSpace: "nowrap", marginLeft: 12 }}
                    >
                      🔗 Open Link
                    </a>
                  )}
                </div>

                {p.description && (
                  <div
                    style={{
                      color: "var(--text-2)",
                      marginTop: 6,
                      fontSize: 14,
                    }}
                  >
                    {p.description}
                  </div>
                )}
                {p.totalTasks > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, color: "var(--text-3)", marginBottom: 5 }}>
                      <span>{p.openTasks} open · {p.totalTasks - p.openTasks} completed</span>
                      <span style={{ fontWeight: 700, color: p.progressPercent >= 70 ? "#16a34a" : p.progressPercent >= 30 ? "#b45309" : "#dc2626" }}>
                        {p.progressPercent}%
                      </span>
                    </div>
                    <div style={{ height: 4, borderRadius: 4, background: "var(--border-strong)", overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${p.progressPercent}%`, background: p.progressPercent >= 70 ? "#22c55e" : p.progressPercent >= 30 ? "#f59e0b" : "#ef4444", borderRadius: 4, transition: "width 0.4s ease" }} />
                    </div>
                  </div>
                )}
              </div>
            ))}
            <Pagination
              page={currentPage}
              totalPages={totalPages}
              onPageChange={(p) => setPage(safePage(p))}
              total={filteredProjects.length}
              pageSize={PAGE_SIZE}
            />
          </>
        )}
      </div>

      {/* ================= CREATE MODAL ================= */}
      {showCreate && (
        <div className="modal-overlay" onClick={() => { setShowCreate(false); setModalError(""); }}>
          <div
            className="glass-card modal"
            style={{ maxWidth: 480 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>Create Project</h3>
              <button className="modal-close" onClick={() => { setShowCreate(false); setModalError(""); }}>✕</button>
            </div>

            {modalError && (
              <div style={{ marginBottom: 16, padding: "10px 14px", borderRadius: 8, background: "var(--danger-soft)", border: "1px solid var(--danger-border)", color: "var(--danger-text)", fontSize: 13 }}>
                {modalError}
              </div>
            )}

            <div style={{ display: "grid", gap: 18 }}>
              <div>
                <label className="form-label">Project Name</label>
                <input
                  className="form-input"
                  placeholder="Enter project name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>

              <div>
                <label className="form-label">Description (Optional)</label>
                <textarea
                  className="form-input"
                  rows={4}
                  placeholder="Short description about this initiative"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>

              <div>
                <label className="form-label">Reference Link (Optional)</label>
                <input
                  className="form-input"
                  placeholder="https://docs.google.com/... or any link"
                  value={projectUrl}
                  onChange={(e) => setProjectUrl(e.target.value)}
                />
              </div>
            </div>

            <div className="modal-actions">
              <button
                className="btn-ghost"
                style={{ padding: "9px 16px", borderRadius: 10 }}
                onClick={() => { setShowCreate(false); setModalError(""); }}
                disabled={creating}
              >
                Cancel
              </button>

              <button
                className="btn"
                style={{ padding: "9px 20px", borderRadius: 10, fontWeight: 600 }}
                onClick={createProject}
                disabled={creating}
              >
                {creating ? "Creating..." : "Create Project"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
